import * as Sentry from '@sentry/react';
import type { Router } from '@tanstack/react-router';
import { ApiError, AuthError } from './errors';

export function initSentry(router: Router<any, any>): void {
  if (!import.meta.env.VITE_SENTRY_ENABLED || !import.meta.env.VITE_SENTRY_DSN) return;

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: 'prod',
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // 20% of transactions for performance monitoring in prod
    tracesSampleRate: 0.2,
    // 5% of sessions for replay
    replaysSessionSampleRate: 0.05,
    // 100% of sessions with errors for replay
    replaysOnErrorSampleRate: 1.0,
  });
}

export function captureException(error: unknown, context?: Record<string, unknown>): void {
  if (!import.meta.env.VITE_SENTRY_ENABLED) return;
  Sentry.captureException(error, context ? { extra: context } : undefined);
}

/**
 * Capture a mutation/query failure, skipping auth errors (user not logged in)
 * and 4xx client errors (invalid input) — those are expected flows, not bugs.
 */
export function captureMutationError(
  error: unknown,
  context: Record<string, unknown>,
): void {
  if (!import.meta.env.VITE_SENTRY_ENABLED) return;
  if (error instanceof AuthError) return;
  if (error instanceof ApiError && error.statusCode >= 400 && error.statusCode < 500) return;
  Sentry.captureException(error, { extra: context });
}

export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: Record<string, unknown>,
): void {
  if (!import.meta.env.VITE_SENTRY_ENABLED) return;
  Sentry.captureMessage(message, { level, extra: context });
}

export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info',
): void {
  if (!import.meta.env.VITE_SENTRY_ENABLED) return;
  Sentry.addBreadcrumb({ message, category, data, level });
}

export function setUser(user: { id: string; email?: string; orgId?: string } | null): void {
  if (!import.meta.env.VITE_SENTRY_ENABLED) return;
  Sentry.setUser(user);
}
